import { FC, useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { SSE } from 'sse.js';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Chat, MessageType } from 'ts-components/Chat';
import { CHAT_BOT } from 'queries/query-keys';
import { toastVariant, useToast } from '__components/ToastProvider';
import MachineLearningService from 'services/MachineLearningService';
import {
  ChatSessionFromAPI,
  ChatStreamType,
  ChatMessageDataStreaming,
} from './types';
import { useChatProgress, actionType } from './useChatState';
import { ChatCard } from './styles';
import { chatMessageMapper, getAnswersOrAlertsByMessage } from './utils';
import { deferExecution } from 'utility/defer';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

type ChatProps = {
  chatSessionId?: string;
  fullScreen?: boolean;
  inModal?: boolean;
  isMobile?: boolean;
  fetchSession: (id?: string) => Promise<ChatSessionFromAPI>;
  chatStream?: ChatStreamType;
  onCompleteSession: (completeSessionId: string) => void;
  getStreamingEndpoint: (id: string) => string;
  onDeleteSession?: (id: string) => void;
  completeSessionLoading?: boolean;
  newChatSessionLoading?: boolean;
  additonalSidePadding?: string;
  topPadding?: string;
  errorMessage?: string;
  stopRefetchAfterDelete?: boolean;
  forceSessionRefetch?: boolean;
  disableSessionRefetch?: () => void;
  entityName?: string;
  eventListenerNames: string[];
  trackEvent?: (event: string, payload?: any) => void;
  mockSession?: Array<any>;
  onMockedSessionInteraction?: (messageToSend: string) => Promise<void>;
};

const DEFFAULT_EVENT_LISTENER = [
  'user_output',
  'alert',
  'refining',
  'question',
];

export const ChatBot: FC<ChatProps> = ({
  chatSessionId = '',
  fullScreen = false,
  inModal = false,
  isMobile = false,
  fetchSession,
  onCompleteSession,
  getStreamingEndpoint,
  onDeleteSession,
  completeSessionLoading,
  newChatSessionLoading = false,
  topPadding,
  additonalSidePadding,
  errorMessage,
  stopRefetchAfterDelete = false,
  forceSessionRefetch, // only to be used to force a refetch at end of session
  disableSessionRefetch,
  entityName,
  eventListenerNames,
  trackEvent,
  mockSession,
  onMockedSessionInteraction,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { state, dispatch } = useChatProgress();
  const { shouldRefreshChatSession, loadingChatSession, isResetting } = state;
  const [showToast] = useToast();
  const [messageSendQueue, setMessageSendQueue] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const resetSendQueue = useCallback(() => {
    setMessageSendQueue([]);
    onMockedSessionInteraction?.('');
  }, [onMockedSessionInteraction]);

  const fetchChatSession = useCallback(async () => {
    try {
      let results;
      if (chatSessionId) {
        results = await fetchSession(chatSessionId);
      } else {
        results = await fetchSession();
      }
      dispatch({ type: actionType.CHAT_SESSION_FETCHED });
      const { messages, answers, alert } = chatMessageMapper(results.messages);
      return {
        ...results,
        messages,
        answers,
        alert,
      };
    } catch (e) {
      return {
        messages: [],
      };
    }
  }, [fetchSession, chatSessionId, dispatch]);

  const {
    data: chatSession,
    refetch,
    isLoading: chatSessionLoading,
  } = useQuery({
    queryKey: CHAT_BOT.CHAT_SESSION(chatSessionId),
    queryFn: fetchChatSession,
    enabled: shouldRefreshChatSession && !mockSession && !newChatSessionLoading,
  }) as any;

  useEffect(() => {
    if (forceSessionRefetch && disableSessionRefetch) {
      refetch();
      deferExecution(() => disableSessionRefetch());
    }
  }, [refetch, forceSessionRefetch, disableSessionRefetch]);

  const deleteChatSessionMutation = useMutation({
    mutationFn: async () => {
      if (!chatSession || !('id' in chatSession) || !onDeleteSession) {
        return;
      }
      return onDeleteSession(chatSession.id);
    },
    onMutate: () => {
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: {
          messages: MessageType[];
          answers: string[];
          alert: string;
        }): {
          messages: MessageType[];
          answers: string[];
          alert: string;
        } => {
          return {
            ...prev,
            messages: [],
            answers: [],
            alert: '',
          };
        }
      );
      dispatch({ type: actionType.RESET_CHAT });
    },
    onSettled: () => {
      resetSendQueue();
      if (!stopRefetchAfterDelete) {
        deferExecution(() =>
          queryClient.invalidateQueries({
            queryKey: CHAT_BOT.CHAT_SESSION(chatSessionId),
          })
        );
      }
    },
  });

  const handleAlert = useCallback(async () => {
    if (!chatSession || !('id' in chatSession)) {
      throw new Error('Chat session ID is undefined');
    }
    const response = await MachineLearningService.cancelChatSession(
      chatSession?.id
    );
    return response ? response.data : [];
  }, [chatSession]);

  const handleAlertMutation = useMutation({
    mutationFn: handleAlert,
  });

  const initiateStream = useCallback(
    async ({
      id,
      message,
      onMessage,
      onRefine,
      onFinal,
      onError,
      onIntialSend,
    }: ChatStreamType) => {
      try {
        setIsStreaming(true);
        onIntialSend();

        const eventSource = new SSE(getStreamingEndpoint(id), {
          payload: JSON.stringify({ text: message }),
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        });

        const allEventListners = [
          ...eventListenerNames,
          ...DEFFAULT_EVENT_LISTENER,
        ];
        allEventListners?.forEach((eventName) => {
          eventSource.addEventListener(eventName, (event: any) => {
            try {
              if (event.data) {
                switch (eventName) {
                  case 'refining':
                    onRefine();
                    break;
                  case 'question':
                    onMessage(event.data);
                    break;
                  case 'alert':
                    onMessage(event.data);
                    break;
                  case 'user_output':
                    onMessage(event.data);
                    break;
                  default:
                    onMessage(event.data, eventName);
                    break;
                }
              }
            } catch (error) {
              monitoringExceptionHelper(error);
            }
          });
        });

        eventSource.addEventListener('final', (event: any) => {
          try {
            if (event.data) {
              onFinal(JSON.parse(event.data));
            }
          } catch (error) {
            monitoringExceptionHelper(error);
          } finally {
            setIsStreaming(false);
          }
        });

        eventSource.onerror = (event: any) => {
          if (onError) {
            onError(event);
          }
        };
      } catch (error) {
        monitoringExceptionHelper(error);
        if (onError) {
          onError(error);
        }
      }
    },
    [eventListenerNames, getStreamingEndpoint]
  );

  const sendMessageWithEventStream = useCallback(
    async (message: string, isDequeue = false) => {
      if (mockSession && !isDequeue) {
        setMessageSendQueue((prev) => [...prev, message]);
        onMockedSessionInteraction?.(message);
        return;
      }

      const onIntialSend = () => {
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (
            prev: { messages: MessageType[] } = { messages: [] }
          ): { messages: MessageType[]; answers: string[] } => {
            return {
              ...prev,
              answers: [],
              messages: [
                ...prev.messages,
                {
                  id: 'temp',
                  messageContent: message,
                  isUserMessage: true,
                  isResponseLoading: false,
                  isCollapsable: false,
                },
                {
                  id: 'loading',
                  messageContent: '',
                  isUserMessage: false,
                  isResponseLoading: true,
                  isCollapsable: false,
                },
              ],
            };
          }
        );
      };
      const onMessage = (data: string, generationVar?: string) => {
        const newChunk = JSON.parse(data);
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (
            prev: { messages: MessageType[] } = { messages: [] }
          ): { messages: MessageType[]; answers: string[] } => {
            const lastMessage = prev.messages[prev.messages.length - 1];
            const lastMessageGenerationVar = lastMessage.id.split('-')[1];
            const shouldUpdateLastMessage =
              lastMessage.id === 'streaming' ||
              lastMessageGenerationVar === generationVar;

            const newMessage = {
              id: generationVar ? `streaming-${generationVar}` : 'streaming',
              messageContent: shouldUpdateLastMessage
                ? lastMessage.messageContent + newChunk
                : newChunk,
              isUserMessage: false,
              isResponseLoading: false,
              isCollapsable: generationVar ? true : false,
              header: generationVar
                ?.split('_')
                .map(
                  (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
                )
                .join(' '),
            };

            let newMessages = prev.messages.filter(
              (msg) => msg.id !== 'loading' && msg.id !== 'refining'
            );

            if (shouldUpdateLastMessage && generationVar) {
              const messageIndex = newMessages.findIndex(
                (msg) => msg.id === `streaming-${generationVar}`
              );
              if (messageIndex !== -1) {
                newMessages[messageIndex] = newMessage;
              } else {
                newMessages.push(newMessage);
              }
            } else if (shouldUpdateLastMessage) {
              newMessages = [...prev.messages.slice(0, -1), newMessage];
            } else {
              newMessages.push(newMessage);
            }

            return {
              ...prev,
              answers: [],
              messages: newMessages,
            };
          }
        );
      };

      const onRefine = () => {
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => {
            if (prev.messages[prev.messages.length - 1].id === 'refining') {
              return prev;
            }
            return {
              ...prev,
              answers: [],
              messages: [
                ...prev.messages,
                {
                  id: 'refining',
                  messageContent: '',
                  isUserMessage: false,
                  isResponseRefining: true,
                  isCollapsable: false,
                },
              ],
            };
          }
        );
      };

      const onFinal = (data: ChatMessageDataStreaming) => {
        const { can_be_completed } = data;
        const { answers, alert } = getAnswersOrAlertsByMessage(data);
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (
            prev: { messages: MessageType[] } = { messages: [] }
          ): {
            messages: MessageType[];
            answers: string[];
            alert: string;
            can_be_completed: boolean;
          } => {
            // here we update messages ids to be completed so we do not modify again
            const newMessages = prev.messages.reduce(
              (acc: MessageType[], msg, index) => {
                if (msg.id !== 'refining' && msg.id !== 'loading') {
                  if (msg.id === 'temp') {
                    const updatedMessage = {
                      ...msg,
                      id: `user-message-${index}`,
                    };
                    acc.push(updatedMessage);
                  } else if (msg.id.includes('streaming')) {
                    const generationVar = msg.id.split('-')[1];
                    const updatedMessage = {
                      ...msg,
                      id: `completed-${generationVar}`,
                    };
                    acc.push(updatedMessage);
                  } else {
                    acc.push(msg);
                  }
                }
                return acc;
              },
              []
            );
            return {
              ...prev,
              messages: [...newMessages],
              answers: answers,
              alert,
              can_be_completed,
            };
          }
        );
      };

      const onError = (event: any) => {
        const errorMessage = JSON.parse(event.data);
        showToast({
          message:
            errorMessage.error ??
            t('An error occurred while calling our agent, please try again'),
          variant: toastVariant.FAILURE,
        });
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (
            prev: { messages: MessageType[] } = { messages: [] }
          ): {
            messages: MessageType[];
          } => {
            const newMessages = prev.messages.filter(
              (msg) => msg.id !== 'refining' && msg.id !== 'loading'
            );
            return {
              ...prev,
              messages: newMessages.slice(0, -1),
            };
          }
        );
      };

      return await initiateStream({
        id: chatSession.id,
        message,
        onIntialSend,
        onMessage,
        onFinal,
        onRefine,
        onError,
      });
    },
    [
      chatSessionId,
      chatSession,
      queryClient,
      initiateStream,
      showToast,
      t,
      mockSession,
      onMockedSessionInteraction,
    ]
  );

  const chatSessionQueueKey = chatSession?.id;
  const hasDequeued = useRef(false);

  useEffect(() => {
    if (
      chatSessionQueueKey &&
      messageSendQueue.length > 0 &&
      !hasDequeued.current
    ) {
      hasDequeued.current = true;
      sendMessageWithEventStream(messageSendQueue[0], true).finally(
        resetSendQueue
      );
    }
  }, [
    chatSessionQueueKey,
    messageSendQueue,
    sendMessageWithEventStream,
    resetSendQueue,
  ]);

  const alert: string = useMemo(() => {
    if (chatSession && 'alert' in chatSession) {
      return typeof chatSession.alert === 'string' ? chatSession.alert : '';
    }
    return '';
  }, [chatSession]);

  const chatMessagePlaceHolder = useMemo(() => {
    if (chatSession?.can_be_completed) {
      return t('Refine Your Strategic Plan Further');
    } else if (chatSession && chatSession.answer) {
      return t('Custom Answer');
    } else {
      return t('Send a Message');
    }
  }, [chatSession, t]);

  const messages = useMemo(() => {
    if (mockSession) {
      return mockSession;
    }

    if (chatSession && 'messages' in chatSession) {
      return chatSession.messages;
    }

    return [];
  }, [chatSession, mockSession]);

  const autoResponses: string[] = useMemo(() => {
    if (isResetting) {
      return [];
    }
    if (chatSession?.answers) {
      return Array.isArray(chatSession?.answers) ? chatSession?.answers : [];
    }
    return [];
  }, [chatSession, isResetting]);

  const showSkipQuestionsButton: boolean = useMemo(() => {
    return Boolean(
      chatSession &&
        chatSession?.answers?.length > 0 &&
        !chatSession?.can_be_completed
    );
  }, [chatSession]);

  return (
    <ChatCard
      padding={isMobile || fullScreen || inModal ? '0' : '0 100px 0 100px'}
    >
      <Chat
        trackEvent={trackEvent}
        entityName={entityName}
        topPadding={topPadding}
        isMobile={isMobile}
        fullScreen={fullScreen}
        inModal={inModal}
        messages={messages}
        autoResponses={autoResponses}
        alert={alert}
        errorMessage={errorMessage}
        onSendMessage={sendMessageWithEventStream}
        onCreateNewChat={deleteChatSessionMutation.mutate}
        onCompleteSession={() => {
          onCompleteSession(
            chatSessionId !== '' ? chatSessionId : chatSession?.id
          );
        }}
        onAlert={handleAlertMutation.mutate}
        isCompleteSessionLoading={completeSessionLoading || false}
        isLoadingNewSession={
          (messages.length === 0 && !errorMessage) ||
          deleteChatSessionMutation.isPending ||
          loadingChatSession
        }
        isMessageResponseLoading={messageSendQueue.length > 0}
        showCompleteSessionButton={chatSession?.can_be_completed}
        disableCompeteSessionButton={chatSessionLoading || isStreaming}
        disableSkipQuestionsButton={chatSessionLoading || isStreaming}
        disableSendMessageButton={isStreaming}
        showSkipQuestionsButton={showSkipQuestionsButton}
        showAskMoreQuestionsButton={
          (chatSession?.can_be_completed && autoResponses.length === 0) ||
          isStreaming
        }
        placeholder={chatMessagePlaceHolder}
        additonalSidePadding={additonalSidePadding}
      />
    </ChatCard>
  );
};
